import { IconWrapper } from '@/components/shared/IconWrapper';
import { cardBrandLabel } from './card-brand-label';
import type { PaymentMethod } from '@/types/payment-method';
import { PaymentMethodType } from '@/types/payment-method';
import { Badge, Flex, RadioCard, Text } from '@chakra-ui/react';
import { CreditCard, GraduationCap } from 'iconoir-react';

interface PaymentMethodOptionProps {
  pm: PaymentMethod;
}

// Helper function for formatting payment method text, similar to SelectedPaymentMethodDisplay
const formatPaymentMethodText = (pm: PaymentMethod): string => {
  if (pm.type === PaymentMethodType.Card && pm.brand && pm.last4) {
    const brandName = cardBrandLabel(pm.brand);
    // Use dots instead of "**"
    let baseText = `${brandName} •••• ${pm.last4}`;

    // Add expiration date if available
    if (pm.expMonth && pm.expYear) {
      const month = pm.expMonth.toString().padStart(2, '0');
      const year = pm.expYear.toString().slice(-2);
      baseText += ` (expires ${month}/${year})`;
    }

    return baseText;
  } else if (pm.type === PaymentMethodType.BankTransfer && pm.last4) {
    return `Bank Transfer •••• ${pm.last4}`;
  }
  return 'Payment method'; // Fallback
};

export const PaymentMethodOption: React.FC<PaymentMethodOptionProps> = ({ pm }) => {
  // Use the new formatting function
  const details = formatPaymentMethodText(pm);
  const iconComponent = pm.type === PaymentMethodType.Card ? CreditCard : GraduationCap;
  const surcharge =
    pm.type === PaymentMethodType.Card && pm.brand === 'amex' ? '3.5% SURCHARGE' : null;

  return (
    <RadioCard.Item key={pm.id} value={pm.id} w="full" rounded="lg">
      <RadioCard.ItemHiddenInput />
      <RadioCard.ItemControl py={2.5} px={3} alignItems="center">
        {/* Adjust padding */}
        {/* Move Icon to the left */}
        <IconWrapper icon={iconComponent} boxSize={4} color="primary.focusRing" mr={3} />
        {/* Align Content Vertically */}
        <RadioCard.ItemContent flex={1}>
          <Flex alignItems="center" justifyContent="space-between" w="full">
            {/* Display formatted text */}
            <Text fontSize="15px" fontWeight="medium">
              {details}
            </Text>
            {/* Surcharge badge remains on the right */}
            {surcharge && (
              <Badge size="sm" colorPalette="red" variant="solid">
                {surcharge}
              </Badge>
            )}
          </Flex>
        </RadioCard.ItemContent>
        {/* Radio indicator on the far right */}
        <RadioCard.ItemIndicator ml={3} />
      </RadioCard.ItemControl>
    </RadioCard.Item>
  );
};
